import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotchedWhiteNoise } from '../hooks/useNotchedWhiteNoise';
import { useTherapyMilestoneTracking } from '../hooks/useTherapyMilestoneTracking';
import { analyticsEvents } from '../utils/analytics';
import { hapticFeedback } from '../utils/haptic';
import { isIOS } from '../utils/deviceDetection';

interface NotchedWhiteNoiseMobileProps {
  tinnitusFrequency: number;
}

// Mini visualization component
const MiniNotchVisualizer: React.FC<{ frequency: number; isPlaying: boolean }> = ({ 
  frequency, 
  isPlaying 
}) => {
  const [barHeights, setBarHeights] = React.useState<number[]>(() => 
    Array.from({ length: 15 }, (_, i) => i === 7 ? 0.2 : Math.random() * 0.5 + 0.5)
  );
  
  // Animate bars when playing
  React.useEffect(() => {
    if (!isPlaying) return;
    
    const interval = setInterval(() => {
      setBarHeights(prev => 
        prev.map((_, i) => {
          // Center bar (7) is the notch, keep it low
          if (i === 7) {
            return 0.2; // 20% height for notch
          }
          // Random height for other bars
          return Math.random() * 0.5 + 0.5; // 50-100% height
        })
      );
    }, 200); // Update every 200ms for smooth but not frantic animation
    
    return () => clearInterval(interval);
  }, [isPlaying]);
  
  return (
    <div className="flex justify-center">
      <div className="flex gap-1 items-end h-6 px-4">
        {barHeights.map((height, i) => {
          const isNotch = i === 7; // Center notch (single bar)
          const barHeight = isPlaying ? height : (isNotch ? 0.2 : 0.8);
          
          return (
            <div
              key={i}
              className={`w-2 rounded-t transition-all duration-200 ${
                isPlaying ? 'opacity-100' : 'opacity-60'
              }`}
              style={{ 
                height: `${barHeight * 100}%`,
                backgroundColor: isNotch ? '#ef4444' : '#9333ea' // Red for notch, purple for bars
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

export const NotchedWhiteNoiseMobile: React.FC<NotchedWhiteNoiseMobileProps> = ({
  tinnitusFrequency,
}) => {
  const navigate = useNavigate();
  const [showIOSWarning, setShowIOSWarning] = useState(false);
  
  useEffect(() => {
    // Only show warning for iOS users
    setShowIOSWarning(isIOS());
  }, []);
  
  const {
    isPlaying,
    volume,
    notchFrequency,
    playNotchedNoise,
    stopNotchedNoise,
    setVolume,
    setNotchFrequency,
  } = useNotchedWhiteNoise(tinnitusFrequency, 0.2);

  // Milestone tracking
  const { startTracking, stopTracking } = useTherapyMilestoneTracking();

  const handlePlayStop = () => {
    if (isPlaying) {
      hapticFeedback.audioStop();
      stopNotchedNoise();
      // Stop milestone tracking
      stopTracking();
      analyticsEvents.trackFeatureInteraction('notched_noise', 'stop', { frequency: notchFrequency });
    } else {
      hapticFeedback.audioStart();
      playNotchedNoise();
      // Start milestone tracking for therapy session
      startTracking(notchFrequency);
      analyticsEvents.trackFeatureInteraction('notched_noise', 'play', { frequency: notchFrequency });
    }
  };

  const adjustFrequency = (delta: number) => {
    const newFreq = Math.max(20, Math.min(20000, notchFrequency + delta));
    setNotchFrequency(newFreq);
    hapticFeedback.sliderChange();
  };

  const adjustVolume = (delta: number) => {
    const newVolume = Math.max(0, Math.min(0.5, volume + delta));
    setVolume(newVolume);
    hapticFeedback.sliderChange();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header - matching mobile assessment style */}
      <header className="h-12 px-4 flex items-center justify-between bg-white border-b border-purple-100">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/')}
            className="p-1 -m-1 tap-highlight-none text-purple-700 hover:text-purple-800"
            aria-label="Go back"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="ml-2 text-lg font-semibold text-purple-700">TinnitusTune</h1>
        </div>
        <div className="text-sm text-gray-600">Sound Therapy</div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-start px-4 py-8">
        {/* Title */}
        <h1 className="text-3xl font-bold text-purple-700 mb-8">Your Sound Therapy</h1>

        {/* Big Play/Stop Button - matching purple gradient style */}
        <button
          onClick={handlePlayStop}
          className={`w-full max-w-xs h-32 rounded-2xl font-bold text-2xl text-white transition-all transform active:scale-95 tap-highlight-none shadow-lg ${
            isPlaying 
              ? 'bg-red-500 hover:bg-red-600' 
              : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
          }`}
          aria-label={isPlaying ? 'Stop therapy' : 'Start therapy'}
        >
          {isPlaying ? '⏸ Stop' : '▶ Start'}
        </button>

        {/* Mini Visualization and Status */}
        <div className="mt-8 space-y-3 text-center">
          <MiniNotchVisualizer frequency={notchFrequency} isPlaying={isPlaying} />
          <div className="space-y-1">
            <div className="text-lg font-medium text-purple-700">{notchFrequency} Hz</div>
            <div className="text-gray-600">
              {isPlaying && <span className="animate-pulse text-purple-600">♪ Playing • </span>}
              Volume: {Math.round(volume * 200)}%
            </div>
          </div>
        </div>

        {/* Enhanced Frequency Controls */}
        <div className="w-full max-w-sm mt-6 bg-purple-50 rounded-xl p-6">
          <h3 className="font-semibold text-purple-700 mb-4 text-center">
            Set Your Frequency
          </h3>
          
          {/* Direct Input */}
          <div className="mb-4">
            <input
              type="number"
              value={notchFrequency}
              onChange={(e) => {
                const freq = parseInt(e.target.value);
                if (!isNaN(freq) && freq >= 20 && freq <= 20000) {
                  setNotchFrequency(freq);
                  hapticFeedback.sliderChange();
                }
              }}
              className="w-full p-3 text-center text-xl font-bold border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:outline-none transition-colors"
              min="20"
              max="20000"
            />
            <p className="text-xs text-purple-600 mt-1 text-center">
              Tap to edit directly (20-20,000 Hz)
            </p>
          </div>

          {/* Enhanced Step Controls */}
          <div className="grid grid-cols-6 gap-1 mb-4">
            <button
              onClick={() => adjustFrequency(-1000)}
              className="py-2 bg-purple-100 hover:bg-purple-200 rounded text-xs font-medium text-purple-700 tap-highlight-none active:bg-purple-200 transition-colors"
            >
              -1000
            </button>
            <button
              onClick={() => adjustFrequency(-500)}
              className="py-2 bg-purple-100 hover:bg-purple-200 rounded text-xs font-medium text-purple-700 tap-highlight-none active:bg-purple-200 transition-colors"
            >
              -500
            </button>
            <button
              onClick={() => adjustFrequency(-100)}
              className="py-2 bg-purple-100 hover:bg-purple-200 rounded text-xs font-medium text-purple-700 tap-highlight-none active:bg-purple-200 transition-colors"
            >
              -100
            </button>
            <button
              onClick={() => adjustFrequency(100)}
              className="py-2 bg-purple-100 hover:bg-purple-200 rounded text-xs font-medium text-purple-700 tap-highlight-none active:bg-purple-200 transition-colors"
            >
              +100
            </button>
            <button
              onClick={() => adjustFrequency(500)}
              className="py-2 bg-purple-100 hover:bg-purple-200 rounded text-xs font-medium text-purple-700 tap-highlight-none active:bg-purple-200 transition-colors"
            >
              +500
            </button>
            <button
              onClick={() => adjustFrequency(1000)}
              className="py-2 bg-purple-100 hover:bg-purple-200 rounded text-xs font-medium text-purple-700 tap-highlight-none active:bg-purple-200 transition-colors"
            >
              +1000
            </button>
          </div>

          {/* Common Presets */}
          <div className="grid grid-cols-4 gap-2 mb-6">
            {[2000, 4000, 6000, 8000].map(freq => (
              <button
                key={freq}
                onClick={() => {
                  setNotchFrequency(freq);
                  hapticFeedback.buttonTap();
                }}
                className={`py-2 rounded font-medium transition-colors tap-highlight-none ${
                  notchFrequency === freq
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-sm'
                    : 'bg-white border border-purple-200 text-purple-700 hover:bg-purple-100 active:bg-purple-100'
                }`}
              >
                {freq}
              </button>
            ))}
          </div>
        </div>

        {/* Volume Control */}
        <div className="w-full max-w-sm mt-6">
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Volume</h3>
            <div className="flex items-center justify-between">
              <div className="flex gap-1 flex-1">
                {Array.from({ length: 10 }).map((_, i) => (
                  <div
                    key={i}
                    className={`h-6 flex-1 rounded transition-colors ${
                      i < Math.round(volume * 20) 
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500' 
                        : 'bg-purple-100'
                    }`}
                  />
                ))}
              </div>
              <span className="ml-3 text-sm font-medium">{Math.round(volume * 200)}%</span>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <button
                onClick={() => adjustVolume(-0.05)}
                className="py-3 bg-purple-100 hover:bg-purple-200 rounded-lg text-xl font-bold text-purple-700 tap-highlight-none active:bg-purple-200 transition-colors"
              >
                −
              </button>
              <button
                onClick={() => adjustVolume(0.05)}
                className="py-3 bg-purple-100 hover:bg-purple-200 rounded-lg text-xl font-bold text-purple-700 tap-highlight-none active:bg-purple-200 transition-colors"
              >
                +
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">Keep it quiet</p>
          </div>
        </div>

        {/* Quick Tips */}
        <div className="mt-auto pt-8 text-center text-sm text-gray-500 space-y-2">
          <p>Use headphones • 1-2 hours daily • Stop if uncomfortable</p>
          {showIOSWarning ? (
            <p className="text-xs text-yellow-600 font-medium">⚠️ iPhone/iPad: Keep screen on - audio stops when locked</p>
          ) : (
            <p className="text-xs">💡 Tip: You can turn off your screen - the sound keeps playing</p>
          )}
        </div>
      </main>
    </div>
  );
};